import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, Layers, Upload, Play, X, Check, Image as ImageIcon,
  ChevronDown, Loader2, Clock, Sparkles
} from 'lucide-react';
import { toast } from '../utils/toast';
const API = window.__API_BASE;

export default function RecipeComparePage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState([null, null]);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [sharedImage, setSharedImage] = useState(null);
  const [sharedImagePreview, setSharedImagePreview] = useState(null);
  const [sharedText, setSharedText] = useState('');
  const [runs, setRuns] = useState([null, null]);
  const [running, setRunning] = useState([false, false]);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/recipes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRecipes(r.data.recipes || []))
      .catch(() => toast.error('Failed to load recipes'));
  }, [token]);

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSharedImage(file);
    setSharedImagePreview(URL.createObjectURL(file));
  };

  const selectRecipe = (idx, recipe) => {
    const next = [...selectedRecipes];
    next[idx] = recipe;
    setSelectedRecipes(next);
    setDropdownOpen(null);
    setRuns(prev => { const n = [...prev]; n[idx] = null; return n; });
  };

  const runRecipe = async (idx) => {
    const recipe = selectedRecipes[idx];
    if (!recipe) return;
    const newRunning = [...running];
    newRunning[idx] = true;
    setRunning(newRunning);

    try {
      const formData = new FormData();
      if (sharedImage) formData.append('image', sharedImage);
      if (sharedText) formData.append('text_input', sharedText);

      const hasDropdown = recipe.inputs?.some(i => i.type === 'dropdown');
      if (hasDropdown) {
        const defaultOption = recipe.inputs.find(i => i.type === 'dropdown')?.options?.[0];
        if (defaultOption) formData.append('dropdown_value', defaultOption);
      }

      const resp = await axios.post(`${API}/api/recipes/${recipe.slug}/run`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      const runId = resp.data.run_id;
      // Poll for completion
      const poll = async () => {
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const status = await axios.get(`${API}/api/recipes/${recipe.slug}/runs/${runId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (status.data.status === 'completed') {
              setRuns(prev => { const n = [...prev]; n[idx] = status.data; return n; });
              return;
            }
            if (status.data.status === 'failed') {
              toast.error(`${recipe.name} failed: ${status.data.error || 'Unknown'}`);
              return;
            }
            // Update progress
            setRuns(prev => { const n = [...prev]; n[idx] = status.data; return n; });
          } catch { break; }
        }
      };
      await poll();
    } catch (e) {
      toast.error(`Failed to run ${recipe.name}`);
    } finally {
      const nr = [...running];
      nr[idx] = false;
      setRunning(nr);
    }
  };

  const runBoth = async () => {
    if (!selectedRecipes[0] || !selectedRecipes[1]) {
      toast.error('Select 2 recipes to compare');
      return;
    }
    if (!sharedImage && !sharedText) {
      toast.error('Upload an image or enter text as shared input');
      return;
    }
    await Promise.all([runRecipe(0), runRecipe(1)]);
  };

  const canRun = selectedRecipes[0] && selectedRecipes[1] && (sharedImage || sharedText);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0B0F19' }}>
      {/* Header */}
      <header className="h-14 px-4 flex items-center justify-between border-b border-white/[0.06] flex-shrink-0"
        style={{ background: 'rgba(11, 15, 25, 0.8)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/recipes')} className="p-2 hover:bg-white/5 rounded-lg" data-testid="back-to-recipes">
            <ArrowLeft className="w-4 h-4 text-white/60" />
          </button>
          <Layers className="w-5 h-5 text-[#0EA5E9]" />
          <h1 className="text-sm font-semibold text-white">Recipe Compare</h1>
        </div>
        <button
          onClick={runBoth}
          disabled={!canRun || running[0] || running[1]}
          className="px-4 py-2 rounded-lg text-xs font-semibold text-white transition-all disabled:opacity-30"
          style={{ background: canRun && !running[0] && !running[1] ? 'linear-gradient(135deg, #0EA5E9, #38BDF8)' : 'rgba(255,255,255,0.06)' }}
          data-testid="run-compare-btn"
        >
          {running[0] || running[1] ? (
            <><Loader2 className="w-3.5 h-3.5 inline mr-1.5 animate-spin" />Running...</>
          ) : (
            <><Play className="w-3.5 h-3.5 inline mr-1.5" />Run Both</>
          )}
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Shared Input Section */}
        <div className="max-w-3xl mx-auto mb-8">
          <h2 className="text-xs font-mono uppercase tracking-wider text-white/40 mb-3">Shared Input</h2>
          <div className="flex gap-4 flex-wrap">
            {/* Image upload */}
            <div
              onClick={() => fileRef.current?.click()}
              className="w-48 h-32 rounded-xl border-2 border-dashed border-white/10 hover:border-[#0EA5E9]/40 transition-colors flex items-center justify-center cursor-pointer overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.02)' }}
              data-testid="compare-image-upload"
            >
              {sharedImagePreview ? (
                <div className="relative w-full h-full">
                  <img src={sharedImagePreview} alt="Input" className="w-full h-full object-cover" />
                  <button onClick={(e) => { e.stopPropagation(); setSharedImage(null); setSharedImagePreview(null); }}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center">
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-5 h-5 text-white/20 mx-auto mb-1" />
                  <span className="text-[10px] text-white/30">Upload Image</span>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </div>
            {/* Text input */}
            <div className="flex-1 min-w-[200px]">
              <textarea
                value={sharedText}
                onChange={(e) => setSharedText(e.target.value)}
                placeholder="Enter shared text input (scene description, script excerpt, etc.)"
                className="w-full h-32 rounded-xl p-3 text-sm bg-white/[0.03] border border-white/[0.08] text-white placeholder-white/20 focus:outline-none focus:border-[#0EA5E9]/40 resize-none"
                data-testid="compare-text-input"
              />
            </div>
          </div>
        </div>

        {/* Side-by-side Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
          {[0, 1].map(idx => (
            <CompareSlot
              key={idx}
              idx={idx}
              recipes={recipes}
              selected={selectedRecipes[idx]}
              otherSelected={selectedRecipes[1 - idx]}
              onSelect={(r) => selectRecipe(idx, r)}
              run={runs[idx]}
              isRunning={running[idx]}
              dropdownOpen={dropdownOpen === idx}
              onToggleDropdown={() => setDropdownOpen(dropdownOpen === idx ? null : idx)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CompareSlot({ idx, recipes, selected, otherSelected, onSelect, run, isRunning, dropdownOpen, onToggleDropdown }) {
  const imageOutputs = run?.outputs?.filter(o => o.type === 'image') || [];
  const textOutputs = run?.outputs?.filter(o => o.type === 'text') || [];

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      {/* Recipe Selector */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="relative">
          <button
            onClick={onToggleDropdown}
            className="w-full flex items-center justify-between p-3 rounded-lg text-sm transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid={`compare-select-${idx}`}
          >
            <span className={selected ? 'text-white' : 'text-white/30'}>
              {selected ? selected.name : `Select Recipe ${idx + 1}`}
            </span>
            <ChevronDown className="w-4 h-4 text-white/30" />
          </button>
          {dropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 rounded-lg overflow-hidden z-20 max-h-64 overflow-y-auto"
              style={{ background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)' }}>
              {recipes.filter(r => r.slug !== otherSelected?.slug).map(r => (
                <button
                  key={r.slug}
                  onClick={() => onSelect(r)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                  data-testid={`compare-option-${r.slug}`}
                >
                  {r.thumbnail_url ? (
                    <img src={r.thumbnail_url} alt="" className="w-10 h-7 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-7 rounded bg-white/5 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-white truncate">{r.name}</div>
                    <div className="text-[10px] text-white/30">{r.category}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Status / Results */}
      <div className="p-4 min-h-[300px]">
        {isRunning ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Loader2 className="w-8 h-8 text-[#0EA5E9] animate-spin" />
            <span className="text-xs text-white/40">{run?.current_node || 'Processing...'}</span>
          </div>
        ) : run?.status === 'completed' ? (
          <div className="space-y-4">
            {/* Image outputs */}
            {imageOutputs.map((output, i) => (
              <div key={i} className="rounded-lg overflow-hidden">
                <img
                  src={output.url?.startsWith('http') ? output.url : `${API}${output.url}`}
                  alt={output.label}
                  className="w-full rounded-lg"
                  data-testid={`compare-output-image-${idx}-${i}`}
                />
                <p className="text-[10px] text-white/40 mt-1">{output.label}</p>
              </div>
            ))}
            {/* Text outputs */}
            {textOutputs.map((output, i) => (
              <div key={i} className="rounded-lg p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <p className="text-[10px] font-medium text-white/50 mb-1">{output.label}</p>
                <p className="text-xs text-white/70 whitespace-pre-wrap">{output.content}</p>
              </div>
            ))}
          </div>
        ) : run?.status === 'failed' ? (
          <div className="flex flex-col items-center justify-center h-64 gap-2">
            <X className="w-6 h-6 text-red-400" />
            <span className="text-xs text-red-400/60">{run?.error || 'Recipe failed'}</span>
          </div>
        ) : selected ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
            {selected.thumbnail_url && (
              <img src={selected.thumbnail_url} alt="" className="w-32 h-20 rounded-lg object-cover opacity-40" />
            )}
            <p className="text-xs text-white/30">Ready to run <span className="text-white/50 font-medium">{selected.name}</span></p>
            <p className="text-[10px] text-white/20 max-w-xs">{selected.description}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 gap-2">
            <Sparkles className="w-6 h-6 text-white/10" />
            <span className="text-xs text-white/20">Select a recipe to compare</span>
          </div>
        )}
      </div>
    </div>
  );
}
